import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setPageContent } from '@/lib/api/content';
import { uploadImage } from '@/lib/api/media';
import { getStore, refreshStore } from '@/lib/store';
import { logActivity as logActivityDb } from '@/lib/api/activity';

const CONTENT_LANGS = ['de', 'en', 'it', 'fr', 'sq'];

interface ImageEntry {
  key: string;
  label: string;
  category: string;
  url: string;
  pageKey: string;
  lang?: string;
}

interface GalleryImage {
  id: string;
  category: 'dental' | 'surgery' | 'hair' | 'aesthetic';
  beforeImage: string;
  afterImage: string;
  patientLabel: string;
  treatmentLabel: string;
  resultMonths: number;
  description: string;
}

const IMAGE_CATEGORIES = [
  { key: 'hero', labelKey: 'adm2.cat_hero', label: 'Hero & Hintergründe', icon: 'ri-landscape-line' },
  { key: 'services', labelKey: 'adm2.cat_services', label: 'Services / Leistungen', icon: 'ri-service-line' },
  { key: 'about', labelKey: 'adm2.cat_about', label: 'Über uns / Klinik', icon: 'ri-building-line' },
  { key: 'pricing', labelKey: 'adm2.cat_pricing', label: 'Preise / Pakete', icon: 'ri-money-euro-circle-line' },
  { key: 'gallery', labelKey: 'adm2.cat_gallery', label: 'Galerie (Before/After)', icon: 'ri-image-line' },
  { key: 'team', labelKey: 'adm2.cat_team', label: 'Team', icon: 'ri-team-line' },
  { key: 'instagram', labelKey: 'adm2.cat_instagram', label: 'Instagram / Social', icon: 'ri-instagram-line' },
];

const ALL_IMAGES: (ImageEntry & { labelKey: string })[] = [
  { key: 'images.home.hero_bg', labelKey: 'adm2.img_home_hero_bg', label: 'Startseite — Hero Hintergrund', category: 'hero', url: '', pageKey: 'home' },
  { key: 'images.home.cta_bg', labelKey: 'adm2.img_home_cta_bg', label: 'Startseite — CTA Hintergrund', category: 'hero', url: '', pageKey: 'home' },
  { key: 'images.home.process_img', labelKey: 'adm2.img_home_process', label: 'Startseite — Prozess Hintergrund', category: 'hero', url: '', pageKey: 'home' },
  { key: 'images.home.trust_img', labelKey: 'adm2.img_home_trust', label: 'Startseite — Vertrauen Hintergrund', category: 'hero', url: '', pageKey: 'home' },
  { key: 'images.about.hero_bg', labelKey: 'adm2.img_about_hero', label: 'Über uns — Hero (Tirana)', category: 'hero', url: '', pageKey: 'about' },
  { key: 'images.services.hero_bg', labelKey: 'adm2.img_services_hero', label: 'Leistungen — Hero', category: 'hero', url: '', pageKey: 'services' },
  { key: 'images.pricing.hero_bg', labelKey: 'adm2.img_pricing_hero', label: 'Preise — Hero', category: 'hero', url: '', pageKey: 'pricing' },
  { key: 'images.gallery.hero_bg', labelKey: 'adm2.img_gallery_hero', label: 'Galerie — Hero', category: 'hero', url: '', pageKey: 'gallery' },
  { key: 'images.booking.hero_bg', labelKey: 'adm2.img_booking_hero', label: 'Terminbuchung — Hero', category: 'hero', url: '', pageKey: 'booking' },
  { key: 'images.home.services_hair_img', labelKey: 'adm2.img_hair', label: 'Haartransplantation — Bild', category: 'services', url: '', pageKey: 'home' },
  { key: 'images.home.services_dental_img', labelKey: 'adm2.img_dental', label: 'Dental-Kosmetik — Bild', category: 'services', url: '', pageKey: 'home' },
  { key: 'images.home.services_botox_img', labelKey: 'adm2.img_botox', label: 'Botox & Filler — Bild', category: 'services', url: '', pageKey: 'home' },
  { key: 'images.services.hair_img', labelKey: 'adm2.img_services_hair', label: 'Leistungen — Haar', category: 'services', url: '', pageKey: 'services' },
  { key: 'images.services.dental_img', labelKey: 'adm2.img_services_dental', label: 'Leistungen — Dental', category: 'services', url: '', pageKey: 'services' },
  { key: 'images.services.botox_img', labelKey: 'adm2.img_services_botox', label: 'Leistungen — Botox', category: 'services', url: '', pageKey: 'services' },
  { key: 'images.services.surgery_img', labelKey: 'adm2.img_services_surgery', label: 'Leistungen — Chirurgie', category: 'services', url: '', pageKey: 'services' },
  { key: 'images.home.about_img', labelKey: 'adm2.img_about_team', label: 'Über uns — Team Foto', category: 'about', url: '', pageKey: 'home' },
  { key: 'images.about.doctor_img', labelKey: 'adm2.img_about_doctor', label: 'Über uns — Arzt Foto', category: 'about', url: '', pageKey: 'about' },
  { key: 'images.about.mission_img', labelKey: 'adm2.img_about_mission', label: 'Über uns — Mission', category: 'about', url: '', pageKey: 'about' },
  { key: 'images.about.clinic_img', labelKey: 'adm2.img_about_clinic', label: 'Über uns — Klinik', category: 'about', url: '', pageKey: 'about' },
  { key: 'images.pricing.essential_img', labelKey: 'adm2.img_pricing_essential', label: 'Preise — Essential Paket', category: 'pricing', url: '', pageKey: 'pricing' },
  { key: 'images.pricing.premium_img', labelKey: 'adm2.img_pricing_premium', label: 'Preise — Premium Paket', category: 'pricing', url: '', pageKey: 'pricing' },
  { key: 'images.pricing.luxe_img', labelKey: 'adm2.img_pricing_luxe', label: 'Preise — Luxe Paket', category: 'pricing', url: '', pageKey: 'pricing' },
  { key: 'images.pricing.exclusif_img', labelKey: 'adm2.img_pricing_exclusif', label: 'Preise — Exclusif Paket', category: 'pricing', url: '', pageKey: 'pricing' },
  { key: 'images.home.instagram_1', labelKey: 'adm2.img_instagram_1', label: 'Instagram — Bild 1', category: 'instagram', url: '', pageKey: 'home' },
  { key: 'images.home.instagram_2', labelKey: 'adm2.img_instagram_2', label: 'Instagram — Bild 2', category: 'instagram', url: '', pageKey: 'home' },
  { key: 'images.home.instagram_3', labelKey: 'adm2.img_instagram_3', label: 'Instagram — Bild 3', category: 'instagram', url: '', pageKey: 'home' },
  { key: 'images.home.instagram_4', labelKey: 'adm2.img_instagram_4', label: 'Instagram — Bild 4', category: 'instagram', url: '', pageKey: 'home' },
];

const GALLERY_CATEGORIES = [
  { key: 'dental', labelKey: 'adm2.gcat_dental', label: 'Dental-Kosmetik', icon: 'ri-tooth-line' },
  { key: 'surgery', labelKey: 'adm2.gcat_surgery', label: 'Chirurgie', icon: 'ri-heart-pulse-line' },
  { key: 'hair', labelKey: 'adm2.gcat_hair', label: 'Haartransplantation', icon: 'ri-scissors-line' },
  { key: 'aesthetic', labelKey: 'adm2.gcat_aesthetic', label: 'Ästhetik', icon: 'ri-sparkling-line' },
];

const GALLERY_STORAGE_KEY = 'esteva_admin_gallery_images';

export default function AdminImagesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('hero');
  const [images, setImages] = useState<Record<string, string>>();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [saved, setSaved] = useState(false);
  const [showGalleryTab, setShowGalleryTab] = useState(false);

  // Gallery state
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [galleryFilter, setGalleryFilter] = useState<string>('all');
  const [editingGallery, setEditingGallery] = useState<GalleryImage | null>(null);
  const [isAddingGallery, setIsAddingGallery] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryBeforeRef = useRef<HTMLInputElement>(null);
  const galleryAfterRef = useRef<HTMLInputElement>(null);

  const triggerFileUpload = (ref: React.RefObject<HTMLInputElement | null>) => {
    setTimeout(() => {
      ref.current?.click();
    }, 0);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, callback: (url: string) => void) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploadError(null);

    if (file.size > 5 * 1024 * 1024) {
      setImageUploadError(t('adm2.file_too_large_compress'));
      return;
    }

    try {
      const url = await uploadImage(file);
      callback(url);
    } catch {
      setImageUploadError(t('adm2.upload_failed_retry'));
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadImages();
    loadGallery();
  }, [navigate]);

  const loadImages = () => {
    const content = getStore().content;
    const map: Record<string, string> = {};
    ALL_IMAGES.forEach((img) => {
      // read the saved override from any language (German first)
      let val = '';
      for (const lang of CONTENT_LANGS) {
        const v = content[`${img.pageKey}_${lang}`]?.[img.key];
        if (v) { val = v; break; }
      }
      map[img.key] = val;
    });
    setImages(map);
  };

  const loadGallery = () => {
    const stored = localStorage.getItem(GALLERY_STORAGE_KEY);
    if (stored) {
      try {
        setGalleryImages(JSON.parse(stored));
      } catch {
        setGalleryImages([]);
      }
    } else {
      setGalleryImages([]);
    }
  };

  // Persist an image override to page_content for every language.
  const persistImage = async (key: string, value: string) => {
    const imgEntry = ALL_IMAGES.find((img) => img.key === key);
    if (!imgEntry) return;
    await Promise.all(
      CONTENT_LANGS.map((lang) => setPageContent(imgEntry.pageKey, lang, { [key]: value })),
    );
    await logActivityDb('images_updated', `Bild "${imgEntry.label}" aktualisiert`);
    await refreshStore();
  };

  const handleSaveImage = async (key: string) => {
    setImages({ ...images, [key]: editUrl });
    setEditingKey(null);
    showSaved();
    await persistImage(key, editUrl);
    loadImages();
  };

  const handleResetImage = async (key: string) => {
    setImages({ ...images, [key]: '' });
    showSaved();
    await persistImage(key, '');
    loadImages();
  };

  const handleResetAllImages = async () => {
    const map: Record<string, string> = {};
    ALL_IMAGES.forEach((img) => { map[img.key] = ''; });
    setImages(map);
    showSaved();
    await Promise.all(ALL_IMAGES.map((img) => persistImage(img.key, '')));
    loadImages();
  };

  const showSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const startEdit = (key: string) => {
    setEditingKey(key);
    setEditUrl(images[key] || '');
  };

  // Gallery functions
  const handleSaveGallery = () => {
    if (isAddingGallery && editingGallery) {
      const newItem: GalleryImage = {
        ...editingGallery,
        id: 'gallery-' + Date.now(),
      };
      const updated = [...galleryImages, newItem];
      setGalleryImages(updated);
      localStorage.setItem(GALLERY_STORAGE_KEY, JSON.stringify(updated));
    } else if (editingGallery) {
      const updated = galleryImages.map((g) => (g.id === editingGallery.id ? editingGallery : g));
      setGalleryImages(updated);
      localStorage.setItem(GALLERY_STORAGE_KEY, JSON.stringify(updated));
    }
    setEditingGallery(null);
    setIsAddingGallery(false);
    showSaved();
    window.dispatchEvent(new Event('admin-content-updated'));
  };

  const handleDeleteGallery = (id: string) => {
    const updated = galleryImages.filter((g) => g.id !== id);
    setGalleryImages(updated);
    localStorage.setItem(GALLERY_STORAGE_KEY, JSON.stringify(updated));
    showSaved();
    window.dispatchEvent(new Event('admin-content-updated'));
  };

  const handleResetGallery = () => {
    setGalleryImages([]);
    localStorage.setItem(GALLERY_STORAGE_KEY, JSON.stringify([]));
    showSaved();
    window.dispatchEvent(new Event('admin-content-updated'));
  };

  const filteredImages = ALL_IMAGES.filter((img) => img.category === activeCategory);

  const filteredGallery = galleryFilter === 'all'
    ? galleryImages
    : galleryImages.filter((g) => g.category === galleryFilter);

  const activeCatEntry = IMAGE_CATEGORIES.find((c) => c.key === activeCategory);
  const activeCatLabel = activeCatEntry ? t(activeCatEntry.labelKey) : '';

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="min-h-screen bg-background-100">
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 py-3 md:py-0 md:h-16 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer no-underline shrink-0"
            >
              <i className="ri-arrow-left-line text-foreground-600"></i>
            </Link>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/10 shrink-0">
                <i className="ri-image-edit-line text-lg text-accent-600"></i>
              </div>
              <div className="min-w-0">
                <h1 className="font-label text-sm font-semibold text-foreground-900">{t('adm2.manage_images')}</h1>
                <p className="text-[10px] text-foreground-500 font-body break-words">{showGalleryTab ? t('adm2.gallery_comparisons') : `${activeCatLabel} — ${t('adm2.images_count', { uploaded: filteredImages.filter((i) => images[i.key]).length, total: filteredImages.length })}`}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowGalleryTab(!showGalleryTab)}
              className={`px-4 py-1.5 rounded-lg text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
                showGalleryTab
                  ? 'bg-accent-500 text-white'
                  : 'border border-background-200 text-foreground-600 hover:bg-background-100'
              }`}
            >
              <i className={`${showGalleryTab ? 'ri-image-line' : 'ri-contrast-2-line'} mr-1.5`}></i>
              {showGalleryTab ? t('adm2.page_images') : t('adm2.gallery_before_after')}
            </button>
            <Link
              to="/"
              className="text-xs text-foreground-500 hover:text-primary-600 cursor-pointer font-label transition-colors whitespace-nowrap no-underline"
            >
              {t('adm2.to_website')}
            </Link>
          </div>
        </div>
        {saved && (
          <div className="bg-green-50 border-t border-green-200 text-center py-2">
            <span className="text-xs text-green-700 font-body">{t('adm2.saved_live')}</span>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {!showGalleryTab ? (
          <>
            {/* Category Tabs */}
            <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
              {IMAGE_CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-50 border border-background-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {t(cat.labelKey)}
                </button>
              ))}
            </div>

            {/* Image Grid */}
            {filteredImages.length === 0 ? (
              <div className="text-center py-16">
                <i className="ri-image-line text-3xl text-foreground-300"></i>
                <p className="mt-2 text-sm text-foreground-500 font-body">{t('adm2.no_images_category')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredImages.map((img) => (
                  <div key={img.key} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden group">
                    <div className="aspect-video bg-background-200 relative overflow-hidden">
                      {images[img.key] ? (
                        <img
                          src={images[img.key]}
                          alt={t(img.labelKey)}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                          <i className="ri-image-add-line text-2xl text-foreground-300"></i>
                          <span className="text-[10px] text-foreground-400 font-label">{t('adm2.no_image')}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <button
                          onClick={() => startEdit(img.key)}
                          className="px-4 py-2 rounded-full bg-white/90 text-foreground-900 text-xs font-label hover:bg-white transition-all cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-pencil-line mr-1.5"></i>
                          {images[img.key] ? t('adm2.edit') : t('adm2.upload')}
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-label font-semibold text-foreground-900 truncate">{t(img.labelKey)}</p>
                      <p className="text-[10px] text-foreground-400 font-mono mt-0.5 truncate">{img.key}</p>
                      {images[img.key] && (
                        <p className="text-[10px] text-accent-600 font-body mt-1 flex items-center gap-1">
                          <i className="ri-check-line"></i>
                          {t('adm2.customized')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reset All Button */}
            {filteredImages.length > 0 && (
              <div className="mt-8 pt-6 border-t border-background-200/70 text-right">
                <button
                  onClick={handleResetAllImages}
                  className="px-4 py-2 rounded-lg border border-red-200 text-xs text-red-500 font-label hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1.5"></i>
                  {t('adm2.reset_all_images')}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Gallery Tab */}
            <div className="mb-4">
              <p className="text-xs text-foreground-500 font-body mb-3">
                {t('adm2.gallery_intro')}
              </p>
            </div>

            {/* Gallery Filter */}
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <button
                onClick={() => setGalleryFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
                  galleryFilter === 'all' ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:bg-background-50 border border-background-200/60'
                }`}
              >
                {t('adm2.filter_all')} ({galleryImages.length})
              </button>
              {GALLERY_CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setGalleryFilter(cat.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
                    galleryFilter === cat.key ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:bg-background-50 border border-background-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-[10px]`}></i>
                  {t(cat.labelKey)}
                </button>
              ))}
              <div className="flex-1"></div>
              <button
                onClick={handleResetGallery}
                className="px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-500 font-label hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1"></i>
                {t('adm2.reset')}
              </button>
              <button
                onClick={() => {
                  setEditingGallery({
                    id: '',
                    category: 'dental',
                    beforeImage: '',
                    afterImage: '',
                    patientLabel: '',
                    treatmentLabel: '',
                    resultMonths: 3,
                    description: '',
                  });
                  setIsAddingGallery(true);
                }}
                className="px-4 py-1.5 rounded-lg bg-accent-500 text-white text-xs font-label hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
              >
                <i className="ri-add-line"></i>
                {t('adm2.new_comparison')}
              </button>
            </div>

            {/* Gallery List */}
            <div className="space-y-3">
              {filteredGallery.length === 0 ? (
                <div className="text-center py-16">
                  <i className="ri-contrast-2-line text-3xl text-foreground-300"></i>
                  <p className="mt-2 text-sm text-foreground-500 font-body">{t('adm2.no_gallery_images_category')}</p>
                </div>
              ) : (
                filteredGallery.map((item) => (
                  <div key={item.id} className="bg-background-50 rounded-xl border border-background-200/70 p-4">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className="flex gap-4">
                      {/* Before Image */}
                      <div className="flex-shrink-0 w-28 h-20 rounded-lg overflow-hidden bg-background-200">
                        {item.beforeImage ? (
                          <img src={item.beforeImage} alt={t('adm2.before')} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <i className="ri-image-line text-sm text-foreground-300"></i>
                            <span className="text-[9px] text-foreground-400 font-label">{t('adm2.before')}</span>
                          </div>
                        )}
                      </div>
                      {/* After Image */}
                      <div className="flex-shrink-0 w-28 h-20 rounded-lg overflow-hidden bg-background-200">
                        {item.afterImage ? (
                          <img src={item.afterImage} alt={t('adm2.after')} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <i className="ri-image-line text-sm text-foreground-300"></i>
                            <span className="text-[9px] text-foreground-400 font-label">{t('adm2.after')}</span>
                          </div>
                        )}
                      </div>
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <span className="text-xs font-label font-semibold text-foreground-900">{item.patientLabel || t('adm2.no_label')}</span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-label ${
                            item.category === 'dental' ? 'bg-primary-100 text-primary-700' :
                            item.category === 'surgery' ? 'bg-accent-100 text-accent-700' :
                            item.category === 'hair' ? 'bg-secondary-100 text-secondary-700' :
                            'bg-foreground-100 text-foreground-600'
                          }`}>
                            {(() => { const c = GALLERY_CATEGORIES.find((c) => c.key === item.category); return c ? t(c.labelKey) : item.category; })()}
                          </span>
                          <span className="text-[10px] text-foreground-400">{t('adm2.months_count', { count: item.resultMonths })}</span>
                        </div>
                        <p className="text-xs text-foreground-600 font-body line-clamp-2">{item.description}</p>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => {
                            setEditingGallery({ ...item });
                            setIsAddingGallery(false);
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteGallery(item.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Image Edit Modal */}
        {editingKey && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-label text-sm font-semibold text-foreground-900">
                  {(() => { const e = ALL_IMAGES.find((img) => img.key === editingKey); return e ? t(e.labelKey) : t('adm2.edit_image'); })()}
                </h3>
                <button
                  onClick={() => setEditingKey(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="aspect-video rounded-xl overflow-hidden bg-background-200 mb-4 flex items-center justify-center">
                {editUrl ? (
                  <img
                    src={editUrl}
                    alt={t('adm2.preview')}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="text-center">
                    <i className="ri-image-add-line text-3xl text-foreground-300"></i>
                    <p className="text-[10px] text-foreground-400 font-label mt-2">{t('adm2.no_image_uploaded')}</p>
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.upload_image')}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, (base64) => setEditUrl(base64))}
                  className="block w-full text-xs text-foreground-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-100 file:text-primary-700 file:font-label hover:file:bg-primary-200 cursor-pointer"
                />
                <p className="text-[10px] text-foreground-400 font-body mt-1">{t('adm2.max_5mb_jpg_png')}</p>
              </div>
              <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-500 mb-2">
                {t('adm2.image_url_optional')}
              </label>
              <input
                type="text"
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-mono focus:outline-none focus:border-primary-400 transition-all mb-1"
                placeholder="https://..."
                autoFocus
              />
              {imageUploadError && (
                <p className="text-[11px] text-red-500 font-body mb-3">{imageUploadError}</p>
              )}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleSaveImage(editingKey)}
                  className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('adm2.save')}
                </button>
                <button
                  onClick={() => handleResetImage(editingKey)}
                  className="px-4 py-2.5 rounded-lg border border-background-200 text-xs text-foreground-600 font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('adm2.delete')}
                </button>
                <button
                  onClick={() => setEditingKey(null)}
                  className="px-4 py-2.5 rounded-lg border border-background-200 text-xs text-foreground-500 font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('adm2.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Edit Modal */}
        {editingGallery && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-label text-sm font-semibold text-foreground-900">
                  {isAddingGallery ? t('adm2.new_before_after') : t('adm2.edit_comparison')}
                </h3>
                <button
                  onClick={() => { setEditingGallery(null); setIsAddingGallery(false); }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.category')}</label>
                  <div className="flex flex-wrap gap-2">
                    {GALLERY_CATEGORIES.map((cat) => (
                      <button
                        key={cat.key}
                        onClick={() => setEditingGallery({ ...editingGallery, category: cat.key as GalleryImage['category'] })}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
                          editingGallery.category === cat.key
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'
                        }`}
                      >
                        <i className={`${cat.icon} text-[10px]`}></i>
                        {t(cat.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.patient_label')}</label>
                  <input
                    type="text"
                    value={editingGallery.patientLabel}
                    onChange={(e) => setEditingGallery({ ...editingGallery, patientLabel: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                    placeholder={t('adm2.patient_label_ph')}
                  />
                </div>

                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.treatment_label')}</label>
                  <input
                    type="text"
                    value={editingGallery.treatmentLabel}
                    onChange={(e) => setEditingGallery({ ...editingGallery, treatmentLabel: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                    placeholder={t('adm2.treatment_label_ph')}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.before_image')}</label>
                    <button
                      type="button"
                      onClick={() => triggerFileUpload(galleryBeforeRef)}
                      className="relative flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-background-300 bg-background-50 hover:bg-background-100 transition-colors cursor-pointer mb-2"
                    >
                      <i className="ri-upload-cloud-2-line text-foreground-400 text-sm"></i>
                      <span className="text-[11px] text-foreground-600 font-label">{t('adm2.upload_image')}</span>
                    </button>
                    <input
                      type="file"
                      ref={galleryBeforeRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e, (base64) => setEditingGallery({ ...editingGallery, beforeImage: base64 }))}
                    />
                    <input
                      type="text"
                      value={editingGallery.beforeImage}
                      onChange={(e) => setEditingGallery({ ...editingGallery, beforeImage: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-background-200 bg-background-50 text-[11px] text-foreground-900 font-mono focus:outline-none focus:border-primary-400 transition-all"
                      placeholder={t('adm2.or_url')}
                    />
                    {editingGallery.beforeImage && (
                      <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-background-200">
                        <img src={editingGallery.beforeImage} alt={t('adm2.before')} className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.after_image')}</label>
                    <button
                      type="button"
                      onClick={() => triggerFileUpload(galleryAfterRef)}
                      className="relative flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-background-300 bg-background-50 hover:bg-background-100 transition-colors cursor-pointer mb-2"
                    >
                      <i className="ri-upload-cloud-2-line text-foreground-400 text-sm"></i>
                      <span className="text-[11px] text-foreground-600 font-label">{t('adm2.upload_image')}</span>
                    </button>
                    <input
                      type="file"
                      ref={galleryAfterRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e, (base64) => setEditingGallery({ ...editingGallery, afterImage: base64 }))}
                    />
                    <input
                      type="text"
                      value={editingGallery.afterImage}
                      onChange={(e) => setEditingGallery({ ...editingGallery, afterImage: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-background-200 bg-background-50 text-[11px] text-foreground-900 font-mono focus:outline-none focus:border-primary-400 transition-all"
                      placeholder={t('adm2.or_url')}
                    />
                    {editingGallery.afterImage && (
                      <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-background-200">
                        <img src={editingGallery.afterImage} alt={t('adm2.after')} className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>

                {imageUploadError && (
                  <p className="text-[11px] text-red-500 font-body">{imageUploadError}</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.result_after_months')}</label>
                    <input
                      type="number"
                      value={editingGallery.resultMonths}
                      onChange={(e) => setEditingGallery({ ...editingGallery, resultMonths: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                      min={1}
                      max={36}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-600 mb-1.5">{t('adm2.description')}</label>
                  <textarea
                    value={editingGallery.description}
                    onChange={(e) => setEditingGallery({ ...editingGallery, description: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all resize-y"
                    placeholder={t('adm2.result_desc_ph')}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-200">
                <button
                  onClick={handleSaveGallery}
                  className="flex-1 py-2.5 rounded-lg bg-accent-500 text-white text-xs font-label hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isAddingGallery ? t('adm2.add_comparison') : t('adm2.save_changes')}
                </button>
                <button
                  onClick={() => { setEditingGallery(null); setIsAddingGallery(false); }}
                  className="px-4 py-2.5 rounded-lg border border-background-200 text-xs text-foreground-500 font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('adm2.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}